import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Image, Modal, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Path, Rect, Stop } from 'react-native-svg';

import { JackpotChipPng } from '../../../assets';
import CloseIcon from '../../../assets/dailyMissions/close.svg';
import VipHero from '../../../assets/storeModal/vipClub/hero.png';
import { goldGlow } from '../../../shared/glowStyles';
import { Package } from '../../../store/types';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import { copyToClipboard } from '../Friends/InviteFriends/shareInvite';
import { formatAmount, formatPrice, getArtwork } from '../Store/StoreRedesignModal';
import { stripeButtonHeight } from '../Store/stripeButtonHeight';
import { useRedesignScale } from './useRedesignScale';

// A checkout sheet in the Daily Missions panel's chrome: the package on top, then one button per
// payment method - or, once EasyPay has issued one, the code to pay with at an EasyPay office.
// Laid out on the 390-wide mobile frame and scaled to the viewport once.
const DESIGN_FRAME_WIDTH = 390;
const DESIGN_MODAL_WIDTH = 358;
// 470 plus the second wallet row (48 + 10), PayPal and Link being stacked.
const DESIGN_MODAL_HEIGHT = 528;
const MAX_SCALE = 1.15;
const VIEWPORT_MARGIN = 48;

/** `link` is Stripe Link: the Stripe checkout, opened with Link offered first. */
export type PaymentProvider = 'stripe' | 'link' | 'paypal' | 'epay' | 'easypay';

export type EasyPayCode = { paymentCode: string; transactionId: string };

const COPIED_RESET_MS = 2000;
/** PayPal above Link, both the sheet's width; Stripe keeps its Link button to 40-55 px. */
const WALLET_BUTTON_HEIGHT = 48;

const FONT = 'Sofia Sans';
const FONT_CONDENSED = 'Sofia Sans Condensed';

const colors = {
	backdrop: 'rgba(0,0,0,0.68)',
	panelBorder: '#FFF908',
	panelBase: '#320400',
	surface: '#1A0603',
	white: '#FFFFFF',
	gold: '#FFC500',
	amount: '#FFE275',
	bannerLine: '#E89200',
	tileFill: '#330E07',
	tileBorder: '#DA600E',
	paypalFill: '#FFC439',
	paypalBorder: '#FFE08A',
	paypalDark: '#003087',
	paypalLight: '#009CDE',
	// Link's own brand green and near-black ink.
	linkFill: '#00D66F',
	linkBorder: '#7DF0B5',
	linkInk: '#011E0F',
	cardBlue: '#3B8CF0',
	cardBlueDark: '#1F5FBF',
	error: '#FF9B8A',
} as const;

const PANEL_FILL: Array<[string, string]> = [
	['0', '#770700'],
	['0.80127', '#320400'],
	['1', '#320400'],
];
// The amount banner fades out to both sides, as in the store's chip ribbons.
const BANNER_FILL: Array<[string, string, number]> = [
	['0', '#1A0603', 0],
	['0.2', '#1A0603', 1],
	['0.8', '#1A0603', 1],
	['1', '#1A0603', 0],
];

/** Link's arrow mark, drawn beside the "link" wordmark. */
const LinkArrow = ({ size, color }: { size: number; color: string }) => (
	<Svg width={size} height={size} viewBox='0 0 24 24'>
		<Path
			d='M4 12h14.5M12.5 5.5 19 12l-6.5 6.5'
			fill='none'
			stroke={color}
			strokeWidth={3.4}
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</Svg>
);

/** What the sheet hands whoever fills its Link slot. */
export type LinkSlot = {
	/** The sheet's own Link button. */
	fallback: React.ReactNode;
	/** The Link button's shape with a spinner, for while a replacement loads. */
	placeholder: React.ReactNode;
	height: number;
	/** While true the wallet buttons show their loading state together. */
	onLoadingChange: (loading: boolean) => void;
};

type Props = {
	pkg: Package | null;
	visible: boolean;
	/** Set once EasyPay has issued a code; the sheet then shows it instead of the methods. */
	easyPay?: EasyPayCode | null;
	easyPayLoading?: boolean;
	easyPayError?: string;
	onSelect: (provider: PaymentProvider) => void;
	onClose: () => void;
	/**
	 * Puts something else in the Link slot - Stripe's own Link button on web - given the sheet's
	 * Link button and the slot's height, which it keeps as the fallback.
	 */
	renderLinkButton?: (slot: LinkSlot) => React.ReactNode;
};

const PaymentCheckoutModal = ({
	pkg,
	visible,
	easyPay,
	easyPayLoading = false,
	easyPayError,
	onSelect,
	onClose,
	renderLinkButton,
}: Props) => {
	const { t } = useTranslation();
	const { width, height } = useSafeViewport();
	const { isDesktop } = useDevice();
	const panelId = useSvgId('checkoutPanel');
	const bannerId = useSvgId('checkoutBanner');

	const desktopModalScale = useRedesignScale({ fitHeight: DESIGN_MODAL_HEIGHT });
	const scale = isDesktop
		? Math.min(desktopModalScale, (height - VIEWPORT_MARGIN) / DESIGN_MODAL_HEIGHT)
		: Math.min(
				width / DESIGN_FRAME_WIDTH,
				(height - VIEWPORT_MARGIN) / DESIGN_MODAL_HEIGHT,
				MAX_SCALE,
			);
	const styles = useMemo(() => createStyles(scale), [scale]);
	const walletHeight = stripeButtonHeight(WALLET_BUTTON_HEIGHT * scale);
	const [copied, setCopied] = useState(false);
	// PayPal waits with Link, so the two buttons appear together rather than one after the other.
	const [walletsLoading, setWalletsLoading] = useState(!!renderLinkButton);

	useEffect(() => {
		setCopied(false);
	}, [easyPay?.paymentCode]);

	useEffect(() => {
		if (!copied) return;

		const timer = setTimeout(() => setCopied(false), COPIED_RESET_MS);
		return () => clearTimeout(timer);
	}, [copied]);

	if (!pkg) return null;

	const handleCopy = async () => {
		if (easyPay && (await copyToClipboard(easyPay.paymentCode))) setCopied(true);
	};

	const isVip = pkg.type === 'VIP_MEMBERSHIP';
	const amount = pkg.chipAmount ?? pkg.chips ?? 0;

	const linkButton = (
		<Pressable
			accessibilityRole='button'
			accessibilityLabel={t('T_SITE_PAYMENT_PAY_WITH_LINK', {
				defaultValue: 'Pay with Link',
			})}
			onPress={() => onSelect('link')}
			style={styles.link}
		>
			<LinkArrow size={walletHeight * 0.46} color={colors.linkInk} />
			<Text style={styles.linkWordmark} numberOfLines={1}>
				link
			</Text>
		</Pressable>
	);

	const linkPlaceholder = (
		<View style={styles.link} accessibilityRole='progressbar'>
			<ActivityIndicator color={colors.linkInk} />
		</View>
	);

	return (
		<Modal
			visible={visible}
			transparent
			animationType='fade'
			onRequestClose={onClose}
			statusBarTranslucent
			navigationBarTranslucent
		>
			<View style={styles.overlay}>
				<Pressable accessibilityRole='button' onPress={onClose} style={styles.backdrop} />

				<View style={styles.panel}>
					<View pointerEvents='none' style={styles.panelFill}>
						<Svg width='100%' height='100%'>
							<Defs>
								<LinearGradient id={panelId} x1='0' y1='0' x2='0' y2='1'>
									{PANEL_FILL.map(([offset, color]) => (
										<Stop key={offset} offset={offset} stopColor={color} />
									))}
								</LinearGradient>
							</Defs>
							<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${panelId})`} />
						</Svg>
					</View>

					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_CLOSE', { defaultValue: 'Close' })}
						onPress={onClose}
						style={styles.closeHitArea}
						hitSlop={8}
					>
						<View style={styles.closeCircle}>
							<CloseIcon width={18 * scale} height={18 * scale} />
						</View>
					</Pressable>

					<View style={styles.banner}>
						<View style={styles.bannerLine} />
						<View style={styles.bannerBody}>
							<View pointerEvents='none' style={StyleSheet.absoluteFill}>
								<Svg width='100%' height='100%'>
									<Defs>
										<LinearGradient id={bannerId} x1='0' y1='0' x2='1' y2='0'>
											{BANNER_FILL.map(([offset, color, opacity]) => (
												<Stop
													key={offset}
													offset={offset}
													stopColor={color}
													stopOpacity={opacity}
												/>
											))}
										</LinearGradient>
									</Defs>
									<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${bannerId})`} />
								</Svg>
							</View>
							{isVip ? (
								<Text numberOfLines={1} style={styles.bannerAmount}>
									{pkg.name}
								</Text>
							) : (
								<>
									<Image source={JackpotChipPng} style={styles.bannerChip} resizeMode='contain' />
									<Text numberOfLines={1} style={styles.bannerAmount}>
										{formatAmount(amount)}
									</Text>
								</>
							)}
						</View>
						<View style={styles.bannerLine} />
					</View>

					<Image
						source={isVip ? VipHero : getArtwork(pkg)}
						style={styles.artwork}
						resizeMode='contain'
					/>

					<Text style={styles.price}>
						{t('T_SITE_SHOP_PRICE', { defaultValue: 'Price:' })}{' '}
						<Text style={styles.priceValue}>{formatPrice(pkg)}</Text>
					</Text>

					{easyPay ? (
						<>
							<View style={styles.details}>
								<Text style={styles.detailLine}>
									{t('T_SITE_PAYMENT_TRANSACTION_ID', { defaultValue: 'Transaction ID:' })}{' '}
									<Text style={styles.detailValue}>{easyPay.transactionId}</Text>
								</Text>
								<Text style={styles.detailLine}>
									{t('T_SITE_PAYMENT_EASYPAY_CODE', { defaultValue: 'Payment code:' })}{' '}
									<Text style={styles.detailValue}>{easyPay.paymentCode}</Text>
								</Text>
							</View>

							<Pressable accessibilityRole='button' onPress={handleCopy} style={styles.copy}>
								<Text style={styles.copyLabel}>
									{copied
										? t('T_SITE_COPIED', { defaultValue: 'Copied!' })
										: t('T_SITE_PAYMENT_COPY_CODE', { defaultValue: 'Copy code' })}
								</Text>
							</Pressable>

							<Text style={styles.instructions}>
								{t('T_SITE_PAYMENT_EASYPAY_INSTRUCTIONS', {
									defaultValue:
										'To pay at an EasyPay office you need the payment code, the amount and the name of the beneficiary - Fish and Chips AD. Please note that the payment code is valid for 7 days after it is generated.',
								})}
							</Text>
						</>
					) : (
						<>
							<View style={styles.wallets}>
								<Pressable
									accessibilityRole='button'
									accessibilityLabel='PayPal'
									accessibilityState={{ busy: walletsLoading }}
									disabled={walletsLoading}
									onPress={() => onSelect('paypal')}
									style={styles.paypal}
								>
									{walletsLoading ? (
										<ActivityIndicator color={colors.paypalDark} />
									) : (
										<Text style={styles.paypalWordmark}>
											<Text style={{ color: colors.paypalDark }}>Pay</Text>
											<Text style={{ color: colors.paypalLight }}>Pal</Text>
										</Text>
									)}
								</Pressable>
								{renderLinkButton ? (
									<View style={styles.linkSlot}>
										{renderLinkButton({
											fallback: linkButton,
											placeholder: linkPlaceholder,
											height: walletHeight,
											onLoadingChange: setWalletsLoading,
										})}
									</View>
								) : (
									linkButton
								)}
							</View>

							<Text style={styles.otherTitle}>
								{t('T_SITE_SHOP_OTHER_PAYMENT_METHODS', { defaultValue: 'Other payment methods' })}
							</Text>

							<View style={styles.tiles}>
								<Pressable
									accessibilityRole='button'
									onPress={() => onSelect('stripe')}
									style={styles.tile}
								>
									<View style={styles.cardIcon}>
										<View style={styles.cardStripe} />
									</View>
									<Text numberOfLines={1} style={styles.tileLabel}>
										{t('T_SITE_PAYMENT_METHOD_CARD_SHORT', { defaultValue: 'Card' })}
									</Text>
								</Pressable>
								<Pressable
									accessibilityRole='button'
									accessibilityLabel='ePay.bg'
									onPress={() => onSelect('epay')}
									style={styles.tile}
								>
									<Text numberOfLines={1} style={styles.epayWordmark}>
										ePay.bg
									</Text>
								</Pressable>
								<Pressable
									accessibilityRole='button'
									accessibilityLabel='EasyPay'
									disabled={easyPayLoading}
									onPress={() => onSelect('easypay')}
									style={styles.tile}
								>
									{easyPayLoading ? (
										<ActivityIndicator color={colors.white} />
									) : (
										// One Text per line: the mark is two stacked words in a box.
										<View style={styles.easyPayMark}>
											<Text style={styles.easyPayMarkLine}>Easy</Text>
											<Text style={styles.easyPayMarkLine}>Pay</Text>
										</View>
									)}
								</Pressable>
							</View>

							{easyPayError ? (
								<Text numberOfLines={2} style={styles.error}>
									{t(easyPayError, {
										defaultValue: t('T_PAYMENT_FAILED', { defaultValue: 'Payment failed.' }),
									})}
								</Text>
							) : null}
						</>
					)}
				</View>
			</View>
		</Modal>
	);
};

const createStyles = (scale: number) => {
	const s = (value: number) => value * scale;
	// The wallet buttons follow Stripe's Link button, which stops growing at 55 px.
	const wallet = stripeButtonHeight(s(WALLET_BUTTON_HEIGHT));

	return StyleSheet.create({
		overlay: {
			flex: 1,
			alignItems: 'center',
			justifyContent: 'center',
		},
		backdrop: {
			...StyleSheet.absoluteFillObject,
			backgroundColor: colors.backdrop,
		},
		panel: {
			width: s(DESIGN_MODAL_WIDTH),
			paddingHorizontal: s(16),
			paddingTop: s(20),
			paddingBottom: s(20),
			alignItems: 'center',
			borderRadius: s(16),
			borderWidth: Math.max(1, s(1.5)),
			borderColor: colors.panelBorder,
			// The gradient fill sits on top of this; the flat colour keeps the shadow layer opaque.
			backgroundColor: colors.panelBase,
			shadowColor: '#FFB800',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.45,
			shadowRadius: s(8),
			elevation: 12,
		},
		panelFill: {
			...StyleSheet.absoluteFillObject,
			borderRadius: s(16),
			overflow: 'hidden',
		},
		closeHitArea: {
			position: 'absolute',
			top: s(8),
			right: s(8),
			width: s(44),
			height: s(44),
			alignItems: 'center',
			justifyContent: 'center',
			zIndex: 2,
		},
		closeCircle: {
			width: s(32),
			height: s(32),
			borderRadius: s(16),
			borderWidth: Math.max(0.5, s(1)),
			borderColor: colors.panelBorder,
			backgroundColor: colors.surface,
			alignItems: 'center',
			justifyContent: 'center',
			...goldGlow,
		},
		banner: {
			width: s(220),
			marginTop: s(12),
		},
		bannerLine: {
			height: Math.max(1, s(2)),
			marginHorizontal: s(8),
			borderRadius: s(1),
			backgroundColor: colors.bannerLine,
		},
		bannerBody: {
			height: s(48),
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			gap: s(8),
			paddingHorizontal: s(16),
		},
		bannerChip: {
			width: s(30),
			height: s(30),
		},
		bannerAmount: {
			flexShrink: 1,
			fontFamily: FONT,
			fontSize: s(32),
			lineHeight: s(38),
			fontWeight: '800',
			color: colors.amount,
			textShadowColor: 'rgba(255, 156, 0, 0.5)',
			textShadowOffset: { width: 0, height: 1 },
			textShadowRadius: 4,
		},
		artwork: {
			width: s(200),
			height: s(150),
			marginVertical: s(12),
		},
		price: {
			fontFamily: FONT,
			fontSize: s(20),
			lineHeight: s(24),
			fontWeight: '600',
			color: colors.white,
			marginBottom: s(14),
		},
		priceValue: {
			fontWeight: '800',
			color: colors.gold,
		},
		// PayPal above Link, the two one-tap wallets, the same size and corners as Stripe's
		// Link button (StripeLinkButton gives it 6 on 48).
		wallets: {
			alignSelf: 'stretch',
			gap: s(10),
		},
		paypal: {
			height: wallet,
			borderRadius: Math.round(wallet / 8),
			borderWidth: Math.max(0.5, s(1)),
			borderColor: colors.paypalBorder,
			backgroundColor: colors.paypalFill,
			alignItems: 'center',
			justifyContent: 'center',
		},
		paypalWordmark: {
			fontFamily: FONT,
			fontSize: wallet * 0.5,
			lineHeight: wallet * 0.58,
			fontWeight: '900',
			fontStyle: 'italic',
		},
		// Holds Stripe's Link button on web, with the sheet's own as its fallback.
		linkSlot: { alignSelf: 'stretch' },
		link: {
			height: wallet,
			borderRadius: Math.round(wallet / 8),
			borderWidth: Math.max(0.5, s(1)),
			borderColor: colors.linkBorder,
			backgroundColor: colors.linkFill,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			gap: s(4),
			paddingHorizontal: s(8),
		},
		linkWordmark: {
			fontFamily: FONT,
			fontSize: wallet * 0.54,
			lineHeight: wallet * 0.62,
			fontWeight: '900',
			letterSpacing: -s(0.5),
			color: colors.linkInk,
		},
		otherTitle: {
			fontFamily: FONT,
			fontSize: s(16),
			lineHeight: s(19.2),
			fontWeight: '600',
			color: colors.white,
			marginTop: s(18),
			marginBottom: s(10),
		},
		tiles: {
			alignSelf: 'stretch',
			flexDirection: 'row',
			gap: s(10),
		},
		tile: {
			flex: 1,
			height: s(52),
			borderRadius: s(12),
			borderWidth: Math.max(0.5, s(1)),
			borderColor: colors.tileBorder,
			backgroundColor: colors.tileFill,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			gap: s(10),
			shadowColor: '#FFB21A',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.55,
			shadowRadius: s(2.5),
			elevation: 3,
		},
		cardIcon: {
			width: s(30),
			height: s(20),
			borderRadius: s(3),
			backgroundColor: colors.cardBlue,
			overflow: 'hidden',
			justifyContent: 'center',
		},
		cardStripe: {
			height: s(4),
			marginTop: -s(4),
			backgroundColor: colors.cardBlueDark,
		},
		tileLabel: {
			fontFamily: FONT,
			fontSize: s(17),
			lineHeight: s(20),
			fontWeight: '700',
			color: colors.white,
		},
		epayWordmark: {
			fontFamily: FONT_CONDENSED,
			fontSize: s(20),
			lineHeight: s(24),
			fontWeight: '800',
			color: colors.white,
		},
		easyPayMark: {
			paddingHorizontal: s(4),
			paddingVertical: s(1),
			borderRadius: s(3),
			borderWidth: Math.max(1, s(1.5)),
			borderColor: colors.white,
		},
		easyPayMarkLine: {
			fontFamily: FONT_CONDENSED,
			fontSize: s(13),
			lineHeight: s(14),
			fontWeight: '700',
			color: colors.white,
		},
		error: {
			marginTop: s(10),
			fontFamily: FONT,
			fontSize: s(13),
			lineHeight: s(16),
			fontWeight: '600',
			color: colors.error,
			textAlign: 'center',
		},
		details: {
			alignItems: 'center',
			gap: s(4),
		},
		detailLine: {
			fontFamily: FONT,
			fontSize: s(18),
			lineHeight: s(22),
			fontWeight: '600',
			color: colors.white,
			textAlign: 'center',
		},
		detailValue: {
			fontWeight: '800',
			color: colors.gold,
		},
		copy: {
			marginTop: s(12),
			height: s(36),
			paddingHorizontal: s(20),
			borderRadius: s(18),
			borderWidth: Math.max(0.5, s(1)),
			borderColor: colors.tileBorder,
			backgroundColor: colors.tileFill,
			alignItems: 'center',
			justifyContent: 'center',
		},
		copyLabel: {
			fontFamily: FONT,
			fontSize: s(14),
			lineHeight: s(17),
			fontWeight: '700',
			color: colors.white,
		},
		instructions: {
			marginTop: s(16),
			fontFamily: FONT,
			fontSize: s(14),
			lineHeight: s(19),
			fontWeight: '500',
			color: colors.white,
			textAlign: 'center',
		},
	});
};

export default PaymentCheckoutModal;
